import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuPortal,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ErrorBoundary from "@/ErrorBoundary";
import { useQuery } from "@tanstack/react-query";
import { Team } from "types";
//@ts-ignore
import Twemoji from "react-twemoji";
import { useRef, useState } from "react";
import Icon from "@/icons";
import Tippy from "@tippyjs/react";
import {
    Drawer,
    DrawerContent,
    DrawerTrigger,
} from "@/components/ui/drawer"

export default function EmojiDropdown({ children, emojis, func }: { children: React.ReactNode; emojis: any; func: any }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const teamsRef = useRef<Record<string, HTMLDivElement | null>>({});
    const categoriesRef = useRef<Record<string, HTMLDivElement | null>>({});
    const [hoveredEmoji, setHoveredEmoji] = useState<any>(null);
    const teams = useQuery({
        queryKey: ["teams"],
        queryFn: async () => {
            const data = await fetch("__SITE_BASE__/api/teams");
            return await data.json();
        },
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const handleFunc = (emoji: any) => {
        func(emoji);
        setIsDropdownOpen(false);
    };
    const isPhone = window.innerWidth < 640;

    return (
        <>
            <div className="hidden sm:flex">
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                    <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
                    <ErrorBoundary>
                        <DropdownMenuPortal>
                            <DropdownMenuContent className="hidden sm:flex w-56 p-0 py-0 px-0 min-w-96 relative bg-brand-default rounded-lg border-0 mr-24 sm:ml-28 group">
                                <DropdownMenuGroup>
                                    <Emojis
                                        hoveredEmoji={hoveredEmoji}
                                        setHoveredEmoji={setHoveredEmoji}
                                        emojis={emojis}
                                        func={handleFunc}
                                        searchTerm={searchTerm}
                                        setSearchTerm={setSearchTerm}
                                        teamsRef={teamsRef}
                                        categoriesRef={categoriesRef}
                                        teams={teams}
                                    />
                                </DropdownMenuGroup>
                            </DropdownMenuContent>
                        </DropdownMenuPortal>
                    </ErrorBoundary>
                </DropdownMenu>
            </div>

            <div className="sm:hidden">
                <Drawer open={isDropdownOpen && isPhone} onOpenChange={setIsDropdownOpen}>
                    <DrawerTrigger asChild>{children}</DrawerTrigger>
                    <DrawerContent className="sm:hidden w-full p-0 py-0 px-0 min-w-96 bg-brand-default rounded-lg border-0 mr-24 sm:ml-28 group">
                        <Emojis
                            hoveredEmoji={hoveredEmoji}
                            setHoveredEmoji={setHoveredEmoji}
                            emojis={emojis}
                            func={handleFunc}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            teamsRef={teamsRef}
                            categoriesRef={categoriesRef}
                            teams={teams}
                        />
                    </DrawerContent>
                </Drawer>
            </div>
        </>
    );
}

function Emojis(
    {
        hoveredEmoji,
        setHoveredEmoji,
        emojis,
        func,
        searchTerm,
        setSearchTerm,
        teamsRef,
        categoriesRef,
        teams,
    }: {
        hoveredEmoji: any;
        setHoveredEmoji: any;
        emojis: any;
        func: any;
        searchTerm: string;
        setSearchTerm: any;
        teamsRef: any;
        categoriesRef: any;
        teams: any;
    }
) {

    const filterEmojisByTeam = (team: Team) => {
        return emojis?.filter(
            (emoji: any) => emoji.teamId === team.id && emoji.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const listAllEmojiCategories = (): string[] => {
        const categories = emojis?.filter((emoji: any) => !emoji.teamId).map((emoji: any) => emoji.category);
        return Array.from(new Set(categories));
    };

    const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <>
            <div className="flex gap-2">
                <div
                    className="gap-2 space-y-4 bg-brand-dark p-2 min-w-14 max-h-64 overflow-y-auto"
                    style={{ scrollbarWidth: "none" }}
                >
                    {teams.data?.teams
                        .filter((team: Team) => filterEmojisByTeam(team)?.length > 0)
                        .map((team: Team) => (
                            <Tippy content={team.name} placement="right" key={team.id}>
                                <div
                                    key={team.id}
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={() => scrollTo({ current: teamsRef.current[team.id] })}
                                >
                                    {team.profilePicture ? (
                                        <img src={team.profilePicture} className="w-8 ml-1 h-8 rounded" />
                                    ) : (
                                        <div className="w-8 h-8 ml-1 bg-brand-darkest rounded flex items-center justify-center">
                                            <p className="text-white text-lg font-semibold">{team.name[0]}</p>
                                        </div>
                                    )}
                                </div>
                            </Tippy>
                        ))}

                    {listAllEmojiCategories().map((category: string) => (
                        <div className="flex flex-col items-center justify-center gap-2 cursor-pointer" onClick={() => scrollTo({ current: categoriesRef.current[category] })}>
                            <div key={category} className="flex items-center gap-2">
                                {emojis
                                    .filter((emoji: any) => emoji.category === category)
                                    .map((emoji: any) => (
                                        <Tippy content={category} placement="right" key={emoji.id}>
                                            <div>
                                                <Twemoji key={emoji.id} className="w-6" loading="lazy">
                                                    {emoji.emoji}
                                                </Twemoji>
                                            </div>
                                        </Tippy>
                                    ))
                                    .slice(0, 1)}
                            </div>
                        </div>

                    ))}
                </div>

                <div
                    className="flex flex-col gap-2 items-start max-h-64 w-full overflow-y-auto"
                    style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(27 27 29)" }}
                >
                    <div className="py-1 w-full items-start flex justify-start">
                        <input
                            type="text"
                            className="w-80 h-8 px-2 bg-brand-dark text-white rounded-lg focus:outline-none"
                            placeholder="Search for an emoji"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {teams.data?.teams
                        .filter((team: Team) => filterEmojisByTeam(team)?.length > 0)
                        .map((team: Team) => (
                            <div
                                key={team.id}
                                ref={(el) => (teamsRef.current[team.id] = el)}
                                className="flex flex-col mt-2 items-start"
                            >
                                <div className="flex items-center gap-2">
                                    {team.profilePicture ? (
                                        <img src={team.profilePicture} className="w-8 h-8 rounded" />
                                    ) : (
                                        <div className="w-8 h-8 bg-brand-darkest rounded flex items-center justify-center">
                                            <p className="text-white text-lg font-semibold">{team.name[0]}</p>
                                        </div>
                                    )}
                                    <p className="text-white font-semibold">{team.name}</p>
                                </div>

                                <div className="flex flex-wrap gap-2 mt-4">
                                    {filterEmojisByTeam(team).map((emoji: any) => (
                                        <div
                                            key={emoji.id}
                                            onClick={() => func(emoji)}
                                            onPointerEnter={() => setHoveredEmoji(emoji)}
                                            className="flex items-center gap-2 px-1 py-1 cursor-pointer hover:bg-brand-darkest/50 rounded-lg mb-2 transition-all duration-300"
                                        >
                                            <img
                                                src={emoji.url}
                                                className="w-6 h-6 rounded"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                    {listAllEmojiCategories().map((category: string) => (
                        <div
                            key={category}
                            ref={(el) => (categoriesRef.current[category] = el)}
                            className="flex flex-col mt-2 items-start"
                        >
                            <div className="flex items-center gap-2">
                                <Icon name="emoji" size="24" color="white" />
                                <p className="text-white font-semibold">{category}</p>
                            </div>

                            <div className="flex flex-wrap gap-2 mt-4">
                                {emojis
                                    .filter(
                                        (emoji: any) =>
                                            emoji.category === category &&
                                            emoji.aliases[0].toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                    .map((emoji: any) => (
                                        <div
                                            key={emoji.id}
                                            onClick={() => func(emoji)}
                                            onPointerEnter={() => setHoveredEmoji(emoji)}
                                            className="flex items-center cursor-pointer gap-2 px-1 py-1 hover:bg-brand-darkest/50 rounded-lg mb-2 transition-all duration-300"
                                        >
                                            <Twemoji
                                                className="w-6"
                                                key={emoji.id}
                                                loading="lazy"
                                            >
                                                {emoji.emoji}
                                            </Twemoji>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="w-full h-full p-2 bg-brand-dark items-center gap-2">
                <div className="flex items-center gap-2">
                    {hoveredEmoji && (
                        <>
                            {hoveredEmoji.url ? (
                                <img src={hoveredEmoji
                                    .url} className="w-8 h-8 ml-2 rounded" />
                            ) : (
                                <Twemoji
                                    className="w-8 ml-2"
                                    key={hoveredEmoji.id}
                                    loading="lazy"
                                >
                                    {hoveredEmoji.emoji}
                                </Twemoji>
                            )}
                        </>
                    ) || (
                            <div className="w-8 h-8 ml-2 rounded flex items-center justify-center">
                                <Icon name="emoji" color="white" />
                            </div>
                        )}

                    <div className="flex flex-col ml-2">
                        <p className="text-white truncate font-semibold max-w-72">{hoveredEmoji?.name || hoveredEmoji?.aliases?.[0] || "Select an emoji"}</p>
                        <p className="text-gray-400">{hoveredEmoji?.category || teams.data?.teams.find((team: Team) => team.id === hoveredEmoji?.teamId)?.name}</p>
                    </div>
                </div>
            </div>
        </>
    )
}